/*
<purpose>
  Cross-window mutation bus: any window that successfully mutates a ticket announces the
  uuid; every OTHER window refetches what it shows (detail refetch + silent list refresh).
  uuid '*' = broad signal for bulk operations ("lists changed, refetch what you hold").
</purpose>
<dependencies>BroadcastChannel (browser). No app imports — leaf module.</dependencies>
<constraints>
  - Must be a silent no-op where BroadcastChannel is unsupported (jsdom, ancient browsers);
    convergence then falls back to the existing poll interval. Never throw.
  - BroadcastChannel never echoes to the posting window — announcing is inherently
    self-refetch-loop-free. Do not "fix" that by also invoking local listeners.
</constraints>
*/

const CHANNEL_NAME = 'stx-ticket-sync';

interface TicketMutatedMessage { type: 'ticket-mutated'; uuid: string; }

let channel: BroadcastChannel | null = null;
const listeners = new Set<(uuid: string) => void>();

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (ev: MessageEvent) => {
      const msg = ev.data as Partial<TicketMutatedMessage> | null;
      if (msg && msg.type === 'ticket-mutated' && typeof msg.uuid === 'string') {
        listeners.forEach(cb => cb(msg.uuid!));
      }
    };
  }
  return channel;
}

export function announceTicketMutated(uuid: string): void {
  const msg: TicketMutatedMessage = { type: 'ticket-mutated', uuid };
  getChannel()?.postMessage(msg);
}

export function onTicketMutated(cb: (uuid: string) => void): () => void {
  getChannel(); // ensure the message handler exists before first delivery
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}
